import * as React from 'react'
import { useParams } from 'react-router'
import { CheckCircle2, Loader2 } from 'lucide-react'
import type { AllowlistConfirmedDto } from '@lib/dto/test-mail.dto'
import { confirmAllowlistInputSchema } from '@lib/input/test-mail.input'
import { AuthShell } from '@/components/AuthShell'
import { ErrorState } from '@/components/PageState'
import { Button } from '@/components/ui/button'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { publicTestMailService } from '@/services/TestMailService'

/**
 * Where an address owner agrees to receive an organization's test emails.
 * Confirming takes a click, not just opening the link, so mail scanners that
 * prefetch links can't confirm on the owner's behalf.
 */
export function ConfirmTestMailPage() {
  const { token: rawToken } = useParams()
  const parsedToken = confirmAllowlistInputSchema.shape.token.safeParse(rawToken)
  const token = parsedToken.success ? parsedToken.data : null
  const [result, setResult] = React.useState<AllowlistConfirmedDto | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [isPending, setIsPending] = React.useState(false)

  async function confirm(): Promise<void> {
    if (token === null) return
    setError(null)
    setIsPending(true)
    try {
      setResult(await publicTestMailService.confirm(token))
    } catch (failure) {
      setError(apiErrorMessage(failure, 'This link could not be confirmed.'))
    } finally {
      setIsPending(false)
    }
  }

  if (token === null) {
    return (
      <AuthShell title="Receive test emails?">
        <ErrorState message="This link could not be confirmed." />
      </AuthShell>
    )
  }

  if (result) {
    return (
      <AuthShell title="Address confirmed">
        <p className="flex items-start gap-2 text-body text-muted-foreground">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
          {result.email} will now receive {result.tenantName}&apos;s PixelSign test emails, marked [TEST]. You can close
          this page.
        </p>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Receive test emails?"
      description="An organization testing its PixelSign integration asked to send test signing emails to your address. They're marked [TEST] and never legally binding."
    >
      {error ? <p className="mb-3 text-body text-danger">{error}</p> : null}
      <Button disabled={isPending} onClick={() => void confirm()}>
        {isPending ? <Loader2 className="animate-spin" /> : null}
        Confirm my address
      </Button>
      <p className="mt-3 text-caption text-muted-foreground">
        Didn&apos;t expect this? Close this page and nothing will be sent to you.
      </p>
    </AuthShell>
  )
}
